import { Canvas, FabricImage, Point } from "fabric";
import type { TMat2D } from "fabric";
import { type MutableRefObject, useCallback, useEffect, useRef, useState } from "react";

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 4;
const FIT_MARGIN = 0.94;

type VerticalAlign = "center" | "top" | "bottom";

/**
 * 배경 이미지를 캔버스 가용 영역(상단 overlay/하단 ActionBar 제외)에 contain-fit 으로 배치.
 * PO QA 2차 o: viewMode 전환·overlay 출현 시 dynamic refit 용 헬퍼.
 * - verticalAlign "center" (단독 보기): 가용 영역 안에서 0.8 마진 중앙 배치.
 * - verticalAlign "top" (둘 다 모드 아래쪽): 이미지 위쪽이 topPadding 에 anchor — 위 캔버스와 갭 제거.
 */
/**
 * bg refit 후 캔버스 위의 모든 비-배경 오브젝트 좌표를 동기 변환.
 * React effect(setBgFitVersion) 를 기다리지 않고 즉시 처리해
 * 리사이즈·topPadding 변경 중 배경만 이동하고 오브젝트는 구 위치에 남는 깜빡임을 방지.
 */
function repositionObjectsAfterBgRefit(
  canvas: Canvas,
  ns: { width: number; height: number },
  oldLeft: number,
  oldTop: number,
  oldScale: number,
  bgFlag: string,
) {
  const bgImg = canvas
    .getObjects()
    .find((o) => (o as any)[bgFlag] === true) as FabricImage | undefined;
  if (!bgImg) return;
  const newLeft = bgImg.left!;
  const newTop = bgImg.top!;
  const newScale = bgImg.scaleX!;
  if (newLeft === oldLeft && newTop === oldTop && newScale === oldScale) return;

  const oldTlX = oldLeft - (ns.width * oldScale) / 2;
  const oldTlY = oldTop - (ns.height * oldScale) / 2;
  const newTlX = newLeft - (ns.width * newScale) / 2;
  const newTlY = newTop - (ns.height * newScale) / 2;
  const ratio = oldScale > 0 ? newScale / oldScale : 1;

  for (const obj of canvas.getObjects()) {
    if ((obj as any)[bgFlag]) continue;
    obj.set({
      left: (obj.left! - oldTlX) * ratio + newTlX,
      top: (obj.top! - oldTlY) * ratio + newTlY,
      scaleX: obj.scaleX! * ratio,
      scaleY: obj.scaleY! * ratio,
    });
    obj.setCoords();
  }
}

function fitBgToCanvas(
  canvas: Canvas,
  bgImg: FabricImage,
  naturalSize: { width: number; height: number },
  topPadding: number,
  bottomPadding: number,
  verticalAlign: VerticalAlign = "center",
) {
  const canvasW = canvas.getWidth();
  const canvasH = canvas.getHeight();
  const effectiveH = Math.max(canvasH - topPadding - bottomPadding, 1);
  let scale: number;
  if (verticalAlign === "center") {
    // 유효 높이의 80%를 목표로 하되 가로 오버플로우 방지
    const targetScale = (effectiveH * FIT_MARGIN) / naturalSize.height;
    scale = Math.min(targetScale, canvasW / naturalSize.width);
  } else {
    scale = Math.min(canvasW / naturalSize.width, effectiveH / naturalSize.height);
  }
  const scaledH = naturalSize.height * scale;
  let centerY: number;
  if (verticalAlign === "top") {
    centerY = topPadding + scaledH / 2;
  } else if (verticalAlign === "bottom") {
    centerY = canvasH - bottomPadding - scaledH / 2;
  } else {
    centerY = topPadding + effectiveH / 2;
  }
  bgImg.set({
    scaleX: scale,
    scaleY: scale,
    left: canvasW / 2,
    top: centerY,
  });
  bgImg.setCoords();
}

interface UseFloorPlanCanvasOptions {
  imageUrl: string | null;
  /** Optional ref that controls whether left-click panning is enabled.
   *  When the ref value is false, left-click on empty area will NOT start panning.
   *  Middle-button panning always works. Defaults to always enabled. */
  panningEnabledRef?: React.RefObject<boolean>;
  /** PO QA 2차 o: 캔버스 상단을 덮는 overlay 높이 (px). 이미지 fit 시 가용 세로 영역 = canvasH - topPadding. */
  topPadding?: number;
  /** PO QA 2차 o 후속4: 캔버스 하단을 덮는 fixed bar 높이 (px). */
  bottomPadding?: number;
  /** PO QA 2차 o 후속: 둘 다 모드 아래쪽 캔버스는 "top" 으로 anchor 해 위 캔버스와의 갭 제거. 기본 "center". */
  verticalAlign?: VerticalAlign;
}

interface ImageNaturalSize {
  width: number;
  height: number;
}

export function useFloorPlanCanvas({ imageUrl, panningEnabledRef, topPadding = 0, bottomPadding = 0, verticalAlign = "center" }: UseFloorPlanCanvasOptions) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const fabricRef = useRef<Canvas | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [zoom, setZoom] = useState(1);
  const imageNaturalSizeRef = useRef<ImageNaturalSize | null>(null);
  const [imageNaturalSize, setImageNaturalSize] =
    useState<ImageNaturalSize | null>(null);
  // PO QA 2차 o: topPadding/bottomPadding 을 ref 로도 보관 (resize/load 핸들러에서 stale 없이 읽기 위함)
  const topPaddingRef = useRef(topPadding);
  topPaddingRef.current = topPadding;
  const bottomPaddingRef = useRef(bottomPadding);
  bottomPaddingRef.current = bottomPadding;
  const verticalAlignRef = useRef(verticalAlign);
  verticalAlignRef.current = verticalAlign;
  // bg image refit 발생할 때마다 증가 — FloorPlanViewer 의 shape draw effect 가 deps 로 사용해 재계산 트리거
  const [bgFitVersion, setBgFitVersion] = useState(0);
  // B6: 마지막으로 fitBgToCanvas 를 실행한 시점의 verticalAlign.
  // topPadding/bottomPadding 변경(메모 오버레이 등)에선 리핏하지 않고,
  // verticalAlign 변경(viewMode 전환)에서만 리핏하기 위한 가드.
  const lastFitAlignRef = useRef(verticalAlign);

  // Canvas 초기화
  useEffect(() => {
    const canvasEl = canvasRef.current;
    const container = containerRef.current;
    if (!canvasEl || !container) return;

    const { width, height } = container.getBoundingClientRect();
    const canvas = new Canvas(canvasEl, {
      width: width || 800,
      height: height || 600,
      selection: false,
      preserveObjectStacking: true,
      fireRightClick: true,
      stopContextMenu: true,
      uniformScaling: false,  // corner 끌어 가로/세로 자유 변경 (shift 누르면 비율 유지)
    });
    fabricRef.current = canvas;

    // 마우스휠 줌
    canvas.on("mouse:wheel", (opt) => {
      const delta = opt.e.deltaY;
      let newZoom = canvas.getZoom() * (delta > 0 ? 0.95 : 1.05);
      newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, newZoom));
      canvas.zoomToPoint(
        new Point(opt.e.offsetX, opt.e.offsetY),
        newZoom,
      );
      setZoom(newZoom);
      opt.e.preventDefault();
      opt.e.stopPropagation();
    });

    // 패닝: 미들 버튼 또는 빈 영역 좌클릭 드래그
    let isPanning = false;
    let lastPos = { x: 0, y: 0 };
    // 팬 시작 직전의 cursor 를 보관해서 mouse:up 시 복원 (모드별 cursor 보존).
    // hoverCursor 도 함께 다뤄야 pan 중 객체 위에서도 grabbing 이 유지됨 (fabric 은
    // mousemove 마다 target.hoverCursor || canvas.hoverCursor 로 재평가하기 때문).
    let cursorBeforePan: string | undefined;
    let hoverCursorBeforePan: string | undefined;

    canvas.on("mouse:down", (opt) => {
      const e = opt.e as MouseEvent;
      // 참조(다른 카메라) 이벤트소스는 "링크" 성격 — 그 위에서 드래그 시작해도 패닝은 되게.
      // 클릭이면 FloorPlanViewer 가 mouse:up 에서 이동 처리. (__refTarget 없는 캔버스엔 무영향)
      const overRefShape = !!(opt.target as { __refTarget?: unknown } | undefined)?.__refTarget;
      const leftClickAllowed = e.button === 0 && (!opt.target || overRefShape) && (panningEnabledRef?.current ?? true);
      if (e.button === 1 || leftClickAllowed) {
        isPanning = true;
        lastPos = { x: e.clientX, y: e.clientY };
        canvas.selection = false;
        cursorBeforePan = canvas.defaultCursor;
        hoverCursorBeforePan = canvas.hoverCursor;
        canvas.defaultCursor = "grabbing";
        canvas.hoverCursor = "grabbing";
        // 누른 직후 mouse:move 가 아직 안 일어났을 때도 즉시 grabbing 보이도록 CSS 직접 적용
        canvas.setCursor("grabbing");
        canvas.requestRenderAll();
      }
    });

    canvas.on("mouse:move", (opt) => {
      if (!isPanning) return;
      const e = opt.e as MouseEvent;
      const vpt = canvas.viewportTransform;
      vpt[4] += e.clientX - lastPos.x;
      vpt[5] += e.clientY - lastPos.y;
      lastPos = { x: e.clientX, y: e.clientY };
      canvas.requestRenderAll();
    });

    canvas.on("mouse:up", () => {
      if (isPanning) {
        isPanning = false;
        // 모드별 cursor 가 있었으면 그대로 복원, 없으면 pan 가능 신호 "grab" / hover 시 "move"
        canvas.defaultCursor = cursorBeforePan ?? "grab";
        canvas.hoverCursor = hoverCursorBeforePan ?? "move";
        cursorBeforePan = undefined;
        hoverCursorBeforePan = undefined;
        // 캔버스는 애초에 selection:false (드래그 다중선택 marquee 미사용). 패닝 후에도 그대로 유지 —
        // true 로 되돌리면 이후 마커 등에서 드래그 시 파란 선택박스가 뜨는 문제 발생.
        canvas.selection = false;
        canvas.requestRenderAll();
      }
    });

    // ResizeObserver — 컨테이너 크기 변경 시 캔버스 dimension 갱신 + 배경 리핏.
    // TopOverlay 가 position:absolute 이므로 topPadding 변경은 컨테이너 크기에 영향 없음
    // → ResizeObserver 는 실제 레이아웃 변경(모드 전환, 윈도우 리사이즈, flex 정렬)에서만 발동.
    let wasHidden = false;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width: w, height: h } = entry.contentRect;
        if (w === 0 || h === 0) { wasHidden = true; continue; }
        canvas.setDimensions({ width: w, height: h });
        const bgImg = canvas
          .getObjects()
          .find((o) => (o as any).__isFloorPlanBg === true) as FabricImage | undefined;
        const ns = imageNaturalSizeRef.current;
        if (bgImg && ns) {
          // 모드 전환(verticalAlign 변경 또는 hidden→visible): viewport 줌 리셋
          const modeSwitch = wasHidden || verticalAlignRef.current !== lastFitAlignRef.current;
          if (modeSwitch) {
            canvas.setViewportTransform([1, 0, 0, 1, 0, 0] as TMat2D);
            setZoom(1);
            lastFitAlignRef.current = verticalAlignRef.current;
          }
          const oldLeft = bgImg.left!;
          const oldTop = bgImg.top!;
          const oldScale = bgImg.scaleX!;
          fitBgToCanvas(canvas, bgImg, ns, topPaddingRef.current, bottomPaddingRef.current, verticalAlignRef.current);
          repositionObjectsAfterBgRefit(canvas, ns, oldLeft, oldTop, oldScale, "__isFloorPlanBg");
          setBgFitVersion((v) => v + 1);
        }
        wasHidden = false;
        canvas.requestRenderAll();
      }
    });
    ro.observe(container);

    return () => {
      ro.disconnect();
      canvas.dispose();
      fabricRef.current = null;
    };
  }, []);

  // 배경 이미지 로드
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    // 기존 오브젝트 모두 제거 (배경 포함)
    canvas.clear();
    setIsReady(false);
    imageNaturalSizeRef.current = null;
    setImageNaturalSize(null);

    // 줌 리셋
    canvas.setViewportTransform([1, 0, 0, 1, 0, 0] as TMat2D);
    setZoom(1);

    if (!imageUrl) {
      canvas.requestRenderAll();
      return;
    }

    let cancelled = false;

    // crossOrigin: "anonymous"로 먼저 시도 → add-adjust-guide 다운로드 시 캔버스 tainted 방지.
    // S3 CORS 헤더가 없어 실패하면 crossOrigin 없이 재시도 (도면은 보이게, 다운로드는 깨질 수 있음).
    const loadImage = () =>
      FabricImage.fromURL(imageUrl, { crossOrigin: "anonymous" }).catch(() =>
        FabricImage.fromURL(imageUrl),
      );

    loadImage().then((img) => {
      if (cancelled || !fabricRef.current) return;

      const naturalW = img.width!;
      const naturalH = img.height!;
      const ns = { width: naturalW, height: naturalH };
      imageNaturalSizeRef.current = ns;
      setImageNaturalSize(ns);

      img.set({
        originX: "center",
        originY: "center",
        selectable: false,
        evented: false,
      });
      (img as any).__isFloorPlanBg = true;

      // PO QA 2차 o: 가용 영역(top/bottom padding 제외) 기준 contain fit
      fitBgToCanvas(canvas, img, ns, topPaddingRef.current, bottomPaddingRef.current, verticalAlignRef.current);
      lastFitAlignRef.current = verticalAlignRef.current;

      canvas.add(img);
      canvas.sendObjectToBack(img);
      canvas.requestRenderAll();
      setBgFitVersion((v) => v + 1);
      setIsReady(true);
    });

    return () => {
      cancelled = true;
    };
  }, [imageUrl]);

  // B6: verticalAlign 변경(viewMode 전환) 시에만 배경 리핏.
  // topPadding/bottomPadding 변경(메모 오버레이·카메라 선택 등)에선 이미지 크기 유지.
  // 캔버스가 숨겨진 상태(display:none → 0 크기)에서는 리핏 스킵 — ResizeObserver가 visible 전환 시 처리.
  useEffect(() => {
    if (!isReady) return;
    if (verticalAlign === lastFitAlignRef.current) return;
    const canvas = fabricRef.current;
    const ns = imageNaturalSizeRef.current;
    if (!canvas || !ns) return;
    // 캔버스가 숨겨진 상태면 스킵 (visible 전환 시 ResizeObserver 가 리핏)
    if (canvas.getWidth() === 0 || canvas.getHeight() === 0) return;
    const bgImg = canvas
      .getObjects()
      .find((o) => (o as any).__isFloorPlanBg === true) as FabricImage | undefined;
    if (!bgImg) return;
    const oldLeft = bgImg.left!;
    const oldTop = bgImg.top!;
    const oldScale = bgImg.scaleX!;
    // 모드 전환: viewport 줌 리셋 → 정해진 크기로 복원
    canvas.setViewportTransform([1, 0, 0, 1, 0, 0] as TMat2D);
    setZoom(1);
    fitBgToCanvas(canvas, bgImg, ns, topPadding, bottomPadding, verticalAlign);
    repositionObjectsAfterBgRefit(canvas, ns, oldLeft, oldTop, oldScale, "__isFloorPlanBg");
    lastFitAlignRef.current = verticalAlign;
    canvas.requestRenderAll();
    setBgFitVersion((v) => v + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topPadding, bottomPadding, verticalAlign, isReady]);

  // 배경 이미지 찾기 헬퍼
  const getBgImage = useCallback((): FabricImage | null => {
    const canvas = fabricRef.current;
    if (!canvas) return null;
    return (
      (canvas
        .getObjects()
        .find((o) => (o as any).__isFloorPlanBg === true) as FabricImage) ??
      null
    );
  }, []);

  // 배경 이미지 왼쪽 상단 좌표 계산 (origin이 center이므로)
  const getBgTopLeft = useCallback((): { x: number; y: number; scale: number } | null => {
    const ns = imageNaturalSizeRef.current;
    const bgObj = getBgImage();
    if (!ns || !bgObj) return null;

    const scale = bgObj.scaleX!;
    const scaledW = ns.width * scale;
    const scaledH = ns.height * scale;
    // bgObj.left/top은 이미지 중심 좌표 (originX/Y = center)
    return {
      x: bgObj.left! - scaledW / 2,
      y: bgObj.top! - scaledH / 2,
      scale,
    };
  }, [getBgImage]);

  // 좌표 변환: 퍼센트(0-100) → 캔버스 픽셀
  const percentToPixel = useCallback(
    (p: { x: number; y: number }): { x: number; y: number } => {
      const ns = imageNaturalSizeRef.current;
      const tl = getBgTopLeft();
      if (!ns || !tl) return { x: 0, y: 0 };

      return {
        x: (p.x / 100) * ns.width * tl.scale + tl.x,
        y: (p.y / 100) * ns.height * tl.scale + tl.y,
      };
    },
    [getBgTopLeft],
  );

  // 좌표 변환: 캔버스 픽셀 → 퍼센트(0-100)
  const pixelToPercent = useCallback(
    (p: { x: number; y: number }): { x: number; y: number } => {
      const ns = imageNaturalSizeRef.current;
      const tl = getBgTopLeft();
      if (!ns || !tl) return { x: 0, y: 0 };

      return {
        x: ((p.x - tl.x) / (tl.scale * ns.width)) * 100,
        y: ((p.y - tl.y) / (tl.scale * ns.height)) * 100,
      };
    },
    [getBgTopLeft],
  );

  const zoomIn = useCallback(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    const newZoom = Math.min(MAX_ZOOM, canvas.getZoom() + 0.1);
    const center = canvas.getCenterPoint();
    canvas.zoomToPoint(center, newZoom);
    setZoom(newZoom);
  }, []);

  const zoomOut = useCallback(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    const newZoom = Math.max(MIN_ZOOM, canvas.getZoom() - 0.1);
    const center = canvas.getCenterPoint();
    canvas.zoomToPoint(center, newZoom);
    setZoom(newZoom);
  }, []);

  // 배경 이미지 스케일된 너비 계산
  const getBackgroundScaledWidth = useCallback((): number => {
    const ns = imageNaturalSizeRef.current;
    const bgObj = getBgImage();
    if (!ns || !bgObj) return 1500;
    return ns.width * bgObj.scaleX!;
  }, [getBgImage]);

  return {
    fabricRef: fabricRef as MutableRefObject<Canvas | null>,
    canvasRef,
    containerRef,
    isReady,
    zoom,
    zoomIn,
    zoomOut,
    percentToPixel,
    pixelToPercent,
    imageNaturalSize,
    getBackgroundScaledWidth,
    /** PO QA 2차 o: 배경 이미지가 refit 될 때마다 증가. 소비자는 shape/marker draw effect 의 deps 에 포함해 재계산. */
    bgFitVersion,
  };
}
